import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import { API_URL, BASE_URL } from '../config';
import './OfflinePurchasePage.css';

const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Extract a single error message from API response (string, { message }, { title }, { errors }). */
const getErrorMessage = (data, fallback = 'Something went wrong.') => {
  if (data == null) return fallback;
  if (typeof data === 'string') return data;
  if (data.message) return data.message;
  if (data.title) return data.title;
  if (data.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors).flat().find(Boolean);
    if (first) return first;
  }
  return fallback;
};

const OfflinePurchasePage = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sellerCode, setSellerCode] = useState('');
  const [matchedSeller, setMatchedSeller] = useState(null);
  const [sellerSearchError, setSellerSearchError] = useState('');
  const [searchingSeller, setSearchingSeller] = useState(false);
  const [form, setForm] = useState({
    sellerId: '',
    amount: '',
    transactionDate: new Date().toISOString().split('T')[0],
    transactionReference: '',
    receiptImageUrl: ''
  });

  useEffect(() => {
    if (!isLoggedIn || userRole !== 'Customer') {
      navigate('/');
      return;
    }
    fetchData();
  }, [isLoggedIn, userRole, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const subsRes = await axios.get(`${API_URL}/OfflineTransaction/my-submissions`, { headers: getAuthHeader() });
      setSubmissions(Array.isArray(subsRes.data) ? subsRes.data : subsRes.data?.$values || []);
      setError(null);
    } catch (err) {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const searchSellerByCode = async (e) => {
    e?.preventDefault?.();
    const code = sellerCode.trim();
    if (!code) {
      setSellerSearchError('Enter a seller code.');
      setMatchedSeller(null);
      setForm((prev) => ({ ...prev, sellerId: '' }));
      return;
    }
    setSearchingSeller(true);
    setSellerSearchError('');
    try {
      const res = await axios.get(`${API_URL}/OfflineTransaction/sellers/by-code`, {
        headers: getAuthHeader(),
        params: { code }
      });
      const seller = res.data;
      setMatchedSeller(seller);
      setForm((prev) => ({ ...prev, sellerId: String(seller.sellerId) }));
    } catch (err) {
      setMatchedSeller(null);
      setForm((prev) => ({ ...prev, sellerId: '' }));
      setSellerSearchError(getErrorMessage(err.response?.data, 'No seller found for this code.'));
    } finally {
      setSearchingSeller(false);
    }
  };

  const handleSellerCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setSellerCode(value);
    if (matchedSeller) {
      setMatchedSeller(null);
      setForm((prev) => ({ ...prev, sellerId: '' }));
    }
    if (sellerSearchError) setSellerSearchError('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/ImageUpload/receipt`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, receiptImageUrl: res.data.imageUrl }));
    } catch (err) {
      setError(getErrorMessage(err.response?.data, 'Failed to upload receipt.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sellerId = parseInt(form.sellerId, 10);
    const amount = parseFloat(form.amount);
    if (!sellerId || !matchedSeller || amount <= 0) {
      setError('Please search and select a seller by code, and enter a valid amount.');
      return;
    }
    if (!form.receiptImageUrl) {
      setError('Please upload the bill/receipt image.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/OfflineTransaction/submit-as-customer`,
        {
          sellerId,
          amount,
          receiptImageUrl: (form.receiptImageUrl || '').trim(),
          transactionDate: form.transactionDate || new Date().toISOString().split('T')[0],
          transactionReference: (form.transactionReference || '').trim() || null
        },
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json'
          }
        }
      );
      setForm({ sellerId: '', amount: '', transactionDate: new Date().toISOString().split('T')[0], transactionReference: '', receiptImageUrl: '' });
      setSellerCode('');
      setMatchedSeller(null);
      setSellerSearchError('');
      await fetchData();
    } catch (err) {
      setError(getErrorMessage(err.response?.data, 'Failed to submit offline purchase.'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

  if (!isLoggedIn || userRole !== 'Customer') return null;

  return (
    <div className="offline-purchase-wrapper">
      <Header />
      <div className="offline-purchase-container">
        <h1>Submit Offline Purchase</h1>
        <p className="page-subtitle">Purchased from a Yelooo seller at their store? Upload your bill to earn points after admin verification.</p>

        {error && <div className="error-banner">{error}</div>}

        <div className="offline-form-section">
          <h2>Upload Bill & Transaction Details</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="offlineSellerCode">Seller Code *</label>
                <div className="seller-code-search">
                  <input
                    id="offlineSellerCode"
                    type="text"
                    value={sellerCode}
                    onChange={handleSellerCodeChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        searchSellerByCode();
                      }
                    }}
                    placeholder="Enter seller code"
                    autoComplete="off"
                    required={!matchedSeller}
                  />
                  <button
                    type="button"
                    className="search-seller-btn"
                    onClick={searchSellerByCode}
                    disabled={searchingSeller}
                  >
                    {searchingSeller ? 'Searching...' : 'Search'}
                  </button>
                </div>
                {sellerSearchError && <p className="seller-search-error">{sellerSearchError}</p>}
                {matchedSeller && (
                  <div className="seller-found">
                    <span className="seller-found-label">Seller name</span>
                    <span className="seller-found-name">{matchedSeller.sellerName}</span>
                    {matchedSeller.shopName ? (
                      <span className="seller-found-shop">{matchedSeller.shopName}</span>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Amount (₹) *</label>
                <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Transaction Date *</label>
                <input type="date" value={form.transactionDate} onChange={(e) => setForm(prev => ({ ...prev, transactionDate: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Transaction Reference (optional)</label>
                <input type="text" placeholder="Bill no., bank ref, etc." value={form.transactionReference} onChange={(e) => setForm(prev => ({ ...prev, transactionReference: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label>Upload Bill / Receipt Image *</label>
              <div className="upload-area">
                <input type="file" accept="image/*" onChange={handleFileUpload} />
                {form.receiptImageUrl ? (
                  <div className="preview-wrap">
                    <img src={`${BASE_URL}${form.receiptImageUrl}`} alt="Receipt" />
                    <span className="uploaded-tag">Uploaded</span>
                  </div>
                ) : (
                  <p>Click or drop image here (JPG, PNG, max 5MB)</p>
                )}
              </div>
            </div>
            <button type="submit" className="submit-btn" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit'}</button>
          </form>
        </div>

        <div className="my-submissions-section">
          <h2>My Offline Purchases</h2>
          {loading ? (
            <p>Loading...</p>
          ) : submissions.length === 0 ? (
            <p className="no-data">No offline purchases yet.</p>
          ) : (
            <div className="submissions-table-wrap">
              <table className="offline-purchases-table">
                <thead>
                  <tr>
                    <th>Order Number</th>
                    <th>Date</th>
                    <th>Seller ID</th>
                    <th>Seller Name</th>
                    <th>Seller Contact</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.offlineTransactionId}>
                      <td className="col-order-no">{s.orderNumber || 'OFF-' + s.offlineTransactionId}</td>
                      <td>{formatDate(s.transactionDate)}</td>
                      <td>{s.sellerCode || s.SellerCode || s.referralCode || '–'}</td>
                      <td>{s.sellerName || '–'}</td>
                      <td>{s.sellerContactNumber || '–'}</td>
                      <td>₹{Number(s.amount).toFixed(2)}</td>
                      <td><span className={`status-badge ${(s.status || '').toLowerCase()}`}>{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OfflinePurchasePage;
